import clsx from 'clsx';
import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { useEffect, useId, useState } from 'react';
import { mergeStyling } from '../common-functions';
import OdinIcon from '../odin-icon/odin-icon.component';

export interface AccordionProps {
  id?: string;
  isOpen: boolean;
  title: string | ReactNode;
  children: ReactNode;
  hidden?: boolean;
  onClick?: (isOpen: boolean) => void;
  wrapperStyles?: string;
  headerStyles?: string;
  childWrapperStyles?: string;
}

export default function Accordion({
  id,
  isOpen,
  title,
  children,
  hidden,
  onClick,
  wrapperStyles,
  headerStyles,
  childWrapperStyles,
}: AccordionProps) {
  const contentWrapperId = useId();
  const [currentlyOpen, setCurrentlyOpen] = useState(isOpen);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only sync when isOpen prop changes, not when currentlyOpen changes
  useEffect(() => {
    if (isOpen !== currentlyOpen) {
      setCurrentlyOpen(isOpen);
    }
  }, [isOpen]);

  const onAccordionClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    onClick?.(!currentlyOpen);
    setCurrentlyOpen(!currentlyOpen);
  };

  const accordionWrapperStyles = clsx('mb-4 flex w-full flex-col', {
    hidden: hidden,
  });

  const accordionHeaderStyles = mergeStyling(
    'flex border-b border-solid border-gray-2 focus:outline-hidden focus:border-primary-3 focus:border-b-2 focus:-mb-0.5',
    headerStyles
  );

  const childWrapperStyling = mergeStyling('grow pt-5', childWrapperStyles);

  const defaultStyle: CSSProperties = {
    maxHeight: currentlyOpen ? '100000px' : '0px',
    display: currentlyOpen ? '' : 'none',
  };

  return (
    <div id={id} className={mergeStyling(accordionWrapperStyles, wrapperStyles)}>
      <a href={`#${contentWrapperId}`} className={accordionHeaderStyles} onClick={onAccordionClick}>
        <div className="grow text-lg leading-9 font-semibold">{title}</div>
        <div className="flex shrink items-center pr-px">
          <OdinIcon icon={currentlyOpen ? 'ChevronUp' : 'ChevronDown'} />
        </div>
      </a>
      <div id={contentWrapperId} className={childWrapperStyling} style={defaultStyle}>
        {children}
      </div>
    </div>
  );
}
